import React from 'react';
import { ContentLayout } from './ContentLayout';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Zap, Layout, Users } from 'lucide-react';
import {
    TestimonialSection,
    FAQSection,
    EnhancedCTASection,
    StepByStepSection,
    Testimonial,
    FAQ,
    Step
} from './SharedSections';
import { fadeInUp, staggerContainer, viewportConfig } from '../../utils/animationVariants';

interface FeaturePageProps {
    slug: string;
    title: string;
    subtitle: string;
    description: string;
    benefits: {
        title: string;
        description: string;
        icon: React.ReactNode;
    }[];
    image: string;
    howItWorks?: Step[];
    testimonials?: Testimonial[];
    faqs?: FAQ[];
    onGetStarted: () => void;
}

export const FeaturePage: React.FC<FeaturePageProps> = ({
    title,
    subtitle,
    description,
    benefits,
    image,
    howItWorks,
    testimonials,
    faqs,
    onGetStarted
}) => {
    return (
        <ContentLayout title={title} description={description} onGetStarted={onGetStarted}>
            {/* Hero Section */}
            <div className="max-w-7xl mx-auto px-6 pt-16 lg:pt-24 pb-12">
                <div className="text-center max-w-4xl mx-auto mb-20">
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={fadeInUp}
                        className="inline-block px-4 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium mb-6"
                    >
                        {subtitle}
                    </motion.div>
                    <motion.h1
                        initial="hidden"
                        animate="visible"
                        variants={fadeInUp}
                        transition={{ delay: 0.1 }}
                        className="text-5xl md:text-7xl font-bold mb-8 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 via-blue-800 to-slate-900 dark:from-white dark:via-blue-200 dark:to-white"
                    >
                        {title}
                    </motion.h1>
                    <motion.p
                        initial="hidden"
                        animate="visible"
                        variants={fadeInUp}
                        transition={{ delay: 0.2 }}
                        className="text-xl md:text-2xl text-slate-600 dark:text-slate-400 leading-relaxed"
                    >
                        {description}
                    </motion.p>
                </div>

                {/* Feature Showcase Grid */}
                <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="relative order-2 lg:order-1"
                    >
                        <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-indigo-500/20 rounded-3xl blur-3xl transform -rotate-3" />
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2">
                            <img
                                src={image}
                                alt={title}
                                className="rounded-2xl w-full object-cover"
                            />
                        </div>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={viewportConfig}
                        variants={staggerContainer}
                        className="space-y-6 order-1 lg:order-2"
                    >
                        {benefits.map((benefit, i) => (
                            <motion.div
                                key={i}
                                variants={fadeInUp}
                                className="flex gap-5 p-6 rounded-2xl bg-white/50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 backdrop-blur-sm hover:shadow-xl hover:bg-white dark:hover:bg-slate-800 transition-all group"
                            >
                                <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                                    {benefit.icon}
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold mb-2 text-slate-900 dark:text-white">{benefit.title}</h3>
                                    <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{benefit.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>

            {/* Dynamic Sections */}
            {howItWorks && (
                <StepByStepSection title="How it Works" steps={howItWorks} />
            )}

            {testimonials && testimonials.length > 0 && (
                <TestimonialSection testimonials={testimonials} />
            )}

            {faqs && faqs.length > 0 && (
                <FAQSection faqs={faqs} />
            )}

            {/* CTA Section */}
            <EnhancedCTASection onGetStarted={onGetStarted} />
        </ContentLayout>
    );
};
